import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import AdminService from "../../services/AdminService";
import { changeAdminRules } from "../../store/actions/AuthActions";

/**
 * Permission slugs come from the API as a flat array on
 * `auth.admin.admin_roles`, e.g. ["view_admin", "add_admin", ...].
 *
 * The backend builds each slug as `<action>_<module or sub-module name>`,
 * where action is one of view / add / edit / delete.
 */
export const ACTIONS = ["view", "add", "edit", "delete"];

export function buildSlug(action, entity) {
	return `${action}_${entity}`;
}

/**
 * Reads permissions out of redux and exposes the four checks.
 *
 * `entity` is the module/sub-module name used when the role was seeded
 * (e.g. "admin", "zone", "parking_slot").
 */
export function usePermissions() {
	const roles = useSelector((state) => state.auth?.auth?.admin?.admin_roles);

	// An admin with no permission array at all is treated as unrestricted, so a
	// misconfigured role can never lock someone out of the whole dashboard.
	const unrestricted = !Array.isArray(roles);
	const list = Array.isArray(roles) ? roles : [];

	const can = (action, entity) => {
		if (!entity) return true;
		if (unrestricted) return true;
		return list.includes(buildSlug(action, entity));
	};

	return {
		roles: list,
		unrestricted,
		can,
		canView: (entity) => can("view", entity),
		canAdd: (entity) => can("add", entity),
		canEdit: (entity) => can("edit", entity),
		canDelete: (entity) => can("delete", entity),
	};
}

/**
 * Re-pulls the logged-in admin's permission slugs and pushes them into redux
 * and localStorage. Call it after saving a role so that editing the role you
 * yourself hold takes effect straight away rather than on next login.
 */
export function useRefreshPermissions() {
	const dispatch = useDispatch();

	return async () => {
		try {
			const res = await new AdminService().getMe();
			const nextRoles = res?.data?.data?.admin_roles;
			if (!Array.isArray(nextRoles)) return null;

			dispatch(changeAdminRules(nextRoles));

			// Keep the persisted session in sync, otherwise a page reload would
			// restore the stale slugs from localStorage.
			const stored = JSON.parse(localStorage.getItem("FMAdmin") || "null");
			if (stored?.admin) {
				stored.admin.admin_roles = nextRoles;
				localStorage.setItem("FMAdmin", JSON.stringify(stored));
			}

			return nextRoles;
		} catch (err) {
			// A failed refresh must never block the save that triggered it.
			return null;
		}
	};
}
